import { useAsyncData, useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { useBatteryConfidenceExperiment } from '~/scopes/product-core/composables/useBatteryConfidenceExperiment/useBatteryConfidenceExperiment'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

export function useFetchPickersInternal(route = useRoute()) {
  const { grade, offerType, productId, closestPickerConfig } =
    useUrlParams(route)
  const $httpFetch = useHttpFetch()
  const { numericVariant } = useBatteryConfidenceExperiment(route)
  const experiments = useExperiments()
  const config = useRuntimeConfig()

  const pickersQueryParams = computed(() => {
    const values: ApiSpecs['/product-page/products/:productId/pickers']['GET']['query'] =
      {
        grade: grade.value ? String(grade.value) : null,
        special_offer_type: offerType.value ? String(offerType.value) : null,
        premium_grade: true,
        cheapest_for_first_picker: config.public
          .FF_PRODUCT_PAGE_ENABLE_CLOSEST_PICKER_CONFIG
          ? !closestPickerConfig.value
          : false,
        battery_confidence_experiment: numericVariant.value,
        fake_sold_out_experiment:
          experiments['experiment.ppOptimisedFakeSoldOut'] ===
            'withOptimisedFakeSoldOut' || null,
        show_out_of_stock_grade_aliases:
          experiments['experiment.ppFakeInStock'] === 'withFakeInStock' || null,
      }

    return removeEmptyValuesInObject(values)
  })

  return useAsyncData(
    () =>
      getAsyncDataKey('pp-pickers', {
        productId,
        ...pickersQueryParams.value,
      }),
    async () => {
      if (!productId) return undefined

      let pickersResponseCacheTagHeader

      const response = await $httpFetch(
        '/product-page/products/:productId/pickers',
        {
          pathParams: { productId },
          query: pickersQueryParams.value,
          onResponse: (context) => {
            if (context.response?.ok) {
              pickersResponseCacheTagHeader =
                context.response?.headers.get('Cache-Tag') ?? ''
            }
          },
        },
      )

      return {
        ...response,
        pickersResponseCacheTagHeader,
      }
    },
    {
      dedupe: 'defer',
      // For a given key, we read from cache. `prefetchAndValidate` will be called when
      // we interact with the pickers, handling the client-side data freshness on every route change.
      getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
    },
  )
}
